import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      toast.success('Welcome back!');
      if (user.is_admin) {
        navigate('/admin');
      } else if (user.is_approved) {
        navigate('/feed');
      } else {
        navigate('/pending');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <div className="text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_gbaian-hub/artifacts/hv9nezmf_GBA%20LOGO%20PNG.png"
              alt="GBA Logo"
              className="h-20 mx-auto mb-4"
            />
            <h1 className="font-oswald text-3xl font-bold text-[#1E3A8A] uppercase tracking-tight">
              GBA Connect
            </h1>
            <p className="text-slate-600 mt-2">Welcome back to the community</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 focus:ring-2 focus:ring-green-500"
                data-testid="email-input"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-semibold text-slate-700">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 focus:ring-2 focus:ring-green-500"
                data-testid="password-input"
                placeholder="••••••••"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1E3A8A] hover:bg-[#172554] text-white font-bold py-3 shadow-lg shadow-blue-900/20"
              data-testid="login-submit-button"
            >
              {loading ? 'Logging in...' : 'Log In'}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <Link
              to="/forgot-password"
              className="text-sm text-[#1E3A8A] hover:text-[#22C55E] font-medium block"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </Link>
            <p className="text-sm text-slate-600">
              New to GBA?{' '}
              <Link to="/signup" className="text-[#22C55E] font-semibold hover:underline" data-testid="signup-link">
                Request Access
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-white/70">
            ENHANCE • ELEVATE • EXCEL
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
